'use client';

import { useCallback } from 'react';
import { DriftClient } from '@drift-labs/sdk';
import { COMMON_UI_UTILS, MAIN_POOL_ID } from '@drift/common';

import { notify } from 'src/utils/notifications';
import { sleep } from 'src/utils/dateTime';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import useDriftAccountsStore from 'src/stores/useDriftAccountsStore';
import TransactionErrorHandler from 'src/utils/TransactionErrorHandler/TransactionErrorHandler';
import useDriftClient from 'src/hooks/useDriftClient';
import { useSetupUserAccountStateAndListeners } from '../../user/useFetchAndSubscribeSubaccounts/useSetupUserAccountStateAndListeners';

interface AccountInitializationParams {
	isReferred: boolean;
	driftClient: DriftClient;
	userIdToInit: number;
	toastId: string;
	runInitializationTransaction: () => Promise<{ txSig: string } | undefined>;
	poolId?: number;
	updatePrevious?: boolean;
	customMessages?: {
		successMessage?: string;
		initializingMessage?: string;
	};
	onSuccess?: (
		userIdToInit: number,
		toastId?: string,
		switchAccount?: boolean
	) => Promise<void>;
}

interface HandleCreateUserSuccessParams {
	userIdToInit: number;
	toastId?: string;
	switchAccount?: boolean;
	customMessages?: {
		successMessage?: string;
	};
}

export const useAccountInitialization = () => {
	const driftClient = useDriftClient();
	const walletAdapter = useDriftStore((s) => s.wallet.current.adapter);
	const isMagicAuth = useDriftStore((s) => s.wallet.isMagicAuth);
	const setStore = useDriftStore((s) => s.set);
	const setAccountsState = useDriftAccountsStore((s) => s.set);
	const { setupUserAccountStateAndListeners } =
		useSetupUserAccountStateAndListeners();

	const handleCreateDriftUserSuccess = useCallback(
		async (params: HandleCreateUserSuccessParams) => {
			const { userIdToInit, toastId, switchAccount, customMessages } = params;

			if (!walletAdapter?.publicKey) return;

			const userKey = COMMON_UI_UTILS.getUserKey(
				userIdToInit,
				walletAdapter.publicKey
			);

			if (switchAccount !== false) {
				setAccountsState((s) => {
					s.currentUserKey = userKey;
				});
			}

			if (isMagicAuth) {
				setStore((s) => {
					s.showMagicLinkFirstTimeHelper = true;
				});
			}

			const newUserAccountPubKey = await driftClient?.getUserAccountPublicKey(
				userIdToInit,
				walletAdapter.publicKey
			);

			setupUserAccountStateAndListeners(newUserAccountPubKey, {
				newAccountId: userIdToInit,
			});

			await driftClient?.userStats?.fetchAccounts();
			const updatedUserStatsAccount = driftClient?.userStats?.getAccount();
			if (updatedUserStatsAccount) {
				setStore((s) => {
					s.userStatsAccount = updatedUserStatsAccount;
				});
			}

			if (toastId) {
				notify({
					type: 'success',
					message: customMessages?.successMessage || 'New subaccount created.',
					id: toastId,
				});
			}

			setAccountsState((s) => {
				s.userAccountNotInitialized = false;
			});

			window.localStorage?.removeItem('referrerParam');
		},
		[
			walletAdapter,
			setAccountsState,
			isMagicAuth,
			setStore,
			driftClient,
			setupUserAccountStateAndListeners,
		]
	);

	const runAccountInitializationTxAndHandling = useCallback(
		async (params: AccountInitializationParams): Promise<boolean> => {
			const {
				isReferred,
				driftClient,
				userIdToInit,
				toastId,
				runInitializationTransaction,
				poolId,
				updatePrevious = false,
				customMessages,
				onSuccess,
			} = params;

			if (!walletAdapter?.publicKey) return false;

			let initializationTxSig: string;

			const result =
				await COMMON_UI_UTILS.initializeAndSubscribeToNewUserAccount(
					driftClient,
					userIdToInit,
					walletAdapter.publicKey,
					{
						initializationStep: async () => {
							try {
								const txResult = await runInitializationTransaction();
								if (!txResult) return false;

								initializationTxSig = txResult.txSig;
								return true;
							} catch (e: any) {
								console.error(e);

								TransactionErrorHandler.handleError({
									error: e,
									toastId,
									wallet: walletAdapter,
								});
								return false;
							}
						},
						postInitializationStep: async () => {
							notify({
								type: 'info',
								message:
									customMessages?.initializingMessage || 'Deposit successful',
								description: 'Subscribing to account...',
								lengthMs: 5000,
								id: toastId,
								updatePrevious,
							});

							if (userIdToInit === 0 && isReferred) {
								notify({
									type: 'success',
									message: 'Referral code applied.',
									updatePrevious,
								});
							}

							setStore((s) => {
								s.referrerParam = null;
								s.referrerParamFailed = false;
							});

							return true;
						},
						handleSuccessStep: async () => {
							// If custom success handler is provided, use it; otherwise use default
							if (onSuccess) {
								await onSuccess(
									userIdToInit,
									toastId,
									poolId && poolId !== MAIN_POOL_ID ? false : true
								);
							} else {
								await handleCreateDriftUserSuccess({
									userIdToInit,
									toastId,
									switchAccount:
										poolId && poolId !== MAIN_POOL_ID ? false : true,
									customMessages,
								});
							}

							await sleep(3000);

							if (initializationTxSig) {
								setAccountsState((s) => {
									s.userAccountNotInitialized = false;
								});
							}

							return true;
						},
					}
				);

			switch (result) {
				case 'ok':
					return true;
				case 'failed_awaitAccountInitializationChainState':
					notify({
						type: 'error',
						description:
							'The operation was successful but unable to subscribe to your new account. Please try refreshing the page.',
						lengthMs: 5000,
					});
					return false;
				case 'failed_handleSuccessStep':
				case 'failed_initializationStep':
				case 'failed_postInitializationStep':
					throw new Error(`Unhandled initialization failure : ${result}`);
			}
		},
		[walletAdapter, setStore, setAccountsState, handleCreateDriftUserSuccess]
	);

	return {
		runAccountInitializationTxAndHandling,
		handleCreateDriftUserSuccess,
	};
};
